import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";

function videoPlacementStyle(activeVideo) {
  return {
    position: "absolute",
    left: `${((activeVideo.placement?.x || 0) / 1920) * 100}%`,
    top: `${((activeVideo.placement?.y || 0) / 1080) * 100}%`,
    width: `${((activeVideo.placement?.width || 640) / 1920) * 100}%`,
    height: `${((activeVideo.placement?.height || 360) / 1080) * 100}%`,
    objectFit: "fill",
    background: "transparent",
    pointerEvents: "none",
  };
}

function alertPlacementStyle(activeAlert) {
  const placement = activeAlert.placement || {};
  const hasPosition = placement.x !== null && placement.x !== undefined && placement.y !== null && placement.y !== undefined;
  const hasSize = placement.width && placement.height;
  if (hasPosition) {
    return {
      position: "absolute",
      left: `${(Number(placement.x) / 1920) * 100}%`,
      top: `${(Number(placement.y) / 1080) * 100}%`,
      width: hasSize ? `${(Number(placement.width) / 1920) * 100}%` : "min(680px, 72vw)",
      minHeight: hasSize ? `${(Number(placement.height) / 1080) * 100}%` : undefined,
      transform: "none",
    };
  }
  return {
    position: "absolute",
    left: "50%",
    top: "18%",
    width: hasSize ? `${(Number(placement.width) / 1920) * 100}%` : "min(680px, 72vw)",
    minHeight: hasSize ? `${(Number(placement.height) / 1080) * 100}%` : undefined,
    transform: "translateX(-50%)",
  };
}

function alertStepPlacementStyle(step) {
  const placement = step.placement || {};
  return {
    position: "absolute",
    left: `${((placement.x ?? 640) / 1920) * 100}%`,
    top: `${((placement.y ?? 360) / 1080) * 100}%`,
    width: `${((placement.width ?? 640) / 1920) * 100}%`,
    minHeight: placement.height ? `${(placement.height / 1080) * 100}%` : "auto",
    pointerEvents: "none",
  };
}

function OverlayPopup({ popup }) {
  const accent = popup.accent_color || "#FFFFFF";
  return (
    <div
      data-testid="overlay-popup"
      style={{
        position: "absolute",
        left: "0.65%",
        top: "1.2%",
        width: "min(900px, 54vw)",
        maxHeight: "15.5vh",
        animation: "blackBarTextIn 320ms ease-out",
        zIndex: 24,
        overflow: "hidden",
        color: "#FFFFFF",
        textShadow: "0 2px 8px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)",
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          borderLeft: `4px solid ${accent}`,
          paddingLeft: 10,
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            fontFamily: "Arial Black, Arial, Helvetica, sans-serif",
            fontSize: "clamp(19px, 1.55vw, 32px)",
            lineHeight: 1.02,
            fontWeight: 900,
            letterSpacing: 0,
            textTransform: "uppercase",
            whiteSpace: "normal",
            overflow: "hidden",
            overflowWrap: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {popup.title}
        </div>
        {popup.message && (
          <div
            style={{
              marginTop: 6,
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "clamp(14px, 0.9vw, 19px)",
              lineHeight: 1.1,
              fontWeight: 700,
              color: "#F2F2F2",
              whiteSpace: "normal",
              overflow: "hidden",
              overflowWrap: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {popup.message}
          </div>
        )}
        {popup.metadata && (
          <div
            style={{
              marginTop: 4,
              color: accent,
              fontSize: "clamp(10px, 0.62vw, 13px)",
              lineHeight: 1,
              fontWeight: 900,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {popup.metadata}
          </div>
        )}
        </div>
    </div>
  );
}

function parseHexColor(value) {
  const color = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#00ff00";
  return {
    r: parseInt(color.slice(1, 3), 16),
    g: parseInt(color.slice(3, 5), 16),
    b: parseInt(color.slice(5, 7), 16),
  };
}

function ChromaKeyVideo({ activeVideo, src, style, onEnded }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const [fallback, setFallback] = useState(false);
  const placementWidth = Math.max(1, Math.round(activeVideo.placement?.width || 640));
  const placementHeight = Math.max(1, Math.round(activeVideo.placement?.height || 360));
  const chroma = activeVideo.chroma_key || {};
  const chromaColor = chroma.color || "#00ff00";
  const chromaSimilarity = Number.isFinite(Number(chroma.similarity)) ? Number(chroma.similarity) : 0.35;
  const chromaSmoothness = Number.isFinite(Number(chroma.smoothness)) ? Number(chroma.smoothness) : 0.08;

  useEffect(() => {
    setFallback(false);
  }, [activeVideo.id]);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || fallback) return undefined;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      setFallback(true);
      return undefined;
    }

    let alive = true;
    const key = parseHexColor(chromaColor);

    const render = () => {
      if (!alive || video.paused || video.ended) return;
      try {
        if (canvas.width !== placementWidth) canvas.width = placementWidth;
        if (canvas.height !== placementHeight) canvas.height = placementHeight;

        context.drawImage(video, 0, 0, placementWidth, placementHeight);
        const frame = context.getImageData(0, 0, placementWidth, placementHeight);
        const pixels = frame.data;
        const softEdge = Math.max(0.001, chromaSmoothness);

        for (let i = 0; i < pixels.length; i += 4) {
          const dr = pixels[i] - key.r;
          const dg = pixels[i + 1] - key.g;
          const db = pixels[i + 2] - key.b;
          const distance = Math.sqrt(dr * dr + dg * dg + db * db) / 441.6729559300637;

          if (distance <= chromaSimilarity) {
            pixels[i + 3] = 0;
          } else if (distance < chromaSimilarity + softEdge) {
            pixels[i + 3] = Math.round(pixels[i + 3] * ((distance - chromaSimilarity) / softEdge));
          }
        }

        context.putImageData(frame, 0, 0);
      } catch (e) {
        setFallback(true);
        return;
      }

      frameRef.current = window.requestAnimationFrame(render);
    };

    const start = () => {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(render);
    };

    video.addEventListener("play", start);
    video.addEventListener("seeked", start);
    video.addEventListener("loadeddata", start);
    start();

    return () => {
      alive = false;
      window.cancelAnimationFrame(frameRef.current);
      video.removeEventListener("play", start);
      video.removeEventListener("seeked", start);
      video.removeEventListener("loadeddata", start);
    };
  }, [activeVideo.id, chromaColor, chromaSimilarity, chromaSmoothness, fallback, placementHeight, placementWidth]);

  return (
    <>
      <video
        ref={videoRef}
        key={`video-${activeVideo.id}`}
        data-testid={fallback ? "overlay-video-media" : "overlay-video-media-source"}
        data-chroma-key="true"
        src={src}
        crossOrigin="anonymous"
        autoPlay
        playsInline
        onEnded={onEnded}
        style={{
          ...style,
          opacity: fallback ? 1 : 0,
        }}
      />
      {!fallback && (
        <canvas
          ref={canvasRef}
          data-testid="overlay-video-media"
          data-chroma-key="true"
          aria-hidden="true"
          style={style}
        />
      )}
    </>
  );
}

function PrizeWheel({ prizes = [], selectedPrize, spinning }) {
  const [started, setStarted] = useState(false);
  const safePrizes = prizes.length ? prizes.slice(0, 16) : ["Prize"];
  const prizeKey = safePrizes.join("|");
  const selectedIndex = Math.max(0, safePrizes.findIndex((prize) => prize === selectedPrize));
  const segment = 360 / safePrizes.length;
  const finalRotation = 1440 + (360 - (selectedIndex * segment + segment / 2));
  const colors = ["#4B1D83", "#F3EAD3", "#7E46CA", "#0A0A0C", "#BF5AF2", "#D9B8FF", "#2B104D", "#C7A3FF"];
  const gradient = safePrizes
    .map((_, index) => {
      const start = index * segment;
      const gapStart = start + 0.9;
      const end = (index + 1) * segment - 0.9;
      return `#050505 ${start}deg ${gapStart}deg, ${colors[index % colors.length]} ${gapStart}deg ${end}deg`;
    })
    .join(", ");

  useEffect(() => {
    setStarted(false);
    if (!spinning) return undefined;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setStarted(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [spinning, selectedPrize, prizeKey]);

  return (
    <div style={{ position: "relative", width: 320, height: 320, margin: "0 auto" }}>
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: -10,
          transform: "translateX(-50%)",
          width: 0,
          height: 0,
          borderLeft: "18px solid transparent",
          borderRight: "18px solid transparent",
          borderTop: "34px solid #F3EAD3",
          zIndex: 2,
          filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.5))",
        }}
      />
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: `
            radial-gradient(circle at 34% 28%, rgba(255,255,255,0.18), transparent 0 18%, transparent 19%),
            repeating-conic-gradient(from 0deg, rgba(243,234,211,0.2) 0deg 0.8deg, transparent 0.8deg ${Math.max(4, segment)}deg),
            conic-gradient(${gradient})
          `,
          border: "8px solid rgba(243,234,211,0.95)",
          boxShadow: "0 18px 55px rgba(0,0,0,0.58), 0 0 42px rgba(191,90,242,0.38), inset 0 0 0 2px rgba(5,5,5,0.62)",
          transform: spinning && started ? `rotate(${finalRotation}deg)` : "rotate(0deg)",
          transition: spinning && started ? "transform 4.8s cubic-bezier(0.12, 0.82, 0.16, 1)" : "none",
        }}
      >
        {safePrizes.map((prize, index) => {
          const angle = index * segment + segment / 2;
          const textColor = ["#F3EAD3", "#0A0A0C", "#FFFFFF", "#F3EAD3", "#FFFFFF", "#0A0A0C", "#F3EAD3", "#0A0A0C"][index % colors.length];
          return (
            <div
              key={`${prize}-${index}`}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: safePrizes.length > 10 ? 66 : 84,
                height: safePrizes.length > 8 ? 24 : 30,
                transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(-83px) rotate(${-angle}deg)`,
                color: textColor,
                fontSize: safePrizes.length > 8 ? 8 : 10,
                fontWeight: 800,
                lineHeight: 1.05,
                textShadow: textColor === "#0A0A0C" ? "0 1px 0 rgba(255,255,255,0.18)" : "0 2px 5px rgba(0,0,0,0.88)",
                whiteSpace: "normal",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                padding: "0 2px",
              }}
            >
              {prize}
            </div>
          );
        })}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 126,
          borderRadius: "50%",
          background: "rgba(5,5,5,0.88)",
          border: "2px solid rgba(243,234,211,0.9)",
          boxShadow: "0 0 0 4px rgba(126,70,202,0.35), 0 8px 18px rgba(0,0,0,0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#F3EAD3",
          fontSize: 12,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        Prize
      </div>
    </div>
  );
}

function RPGDungeonOverlay({ run }) {
  if (!run) return null;
  const latest = run.latest_step;
  const party = run.party || [];
  const defeated = new Set(run.defeated_user_ids || []);
  const display = run.overlay_display || {};
  if (display.enabled === false) return null;
  const position = display.position || "top_right";
  const widthVw = Number(display.width_vw || 38);
  const minWidth = Number(display.min_width_px || 520);
  const maxWidth = Number(display.max_width_px || 720);
  const imageEnabled = display.image_enabled !== false;
  const generatedImageUrl = run.dungeon_art_url
    ? String(run.dungeon_art_url).startsWith("http")
      ? run.dungeon_art_url
      : `${BACKEND_URL || ""}${run.dungeon_art_url}`
    : "";
  const imageUrl = generatedImageUrl || "/rpg-dungeon-fallback.svg";
  const placementStyle = {
    left: position.includes("left") ? `${Number(display.left_vw ?? 2.2)}%` : undefined,
    right: position.includes("right") ? `${Number(display.right_vw ?? 3)}%` : undefined,
    top: position.includes("top") ? `${Number(display.top_vh ?? 4)}%` : undefined,
    bottom: position.includes("bottom") ? `${Number(display.bottom_vh ?? 5.4)}%` : undefined,
  };
  const totalSteps = Math.max(1, Number(run.encounter_count || run.completed_steps || 1));
  const completedSteps = Math.min(totalSteps, Number(run.completed_steps || 0));
  const pct = run.status === "assembling" ? 0 : Math.round((completedSteps / totalSteps) * 100);
  const joinUntil = run.join_window_until ? new Date(run.join_window_until).getTime() : 0;
  const joinSeconds = run.status === "assembling" && joinUntil ? Math.max(0, Math.floor((joinUntil - Date.now()) / 1000)) : 0;
  const lootCount = Object.values(run.loot || {}).reduce((sum, items) => sum + (Array.isArray(items) ? items.length : 0), 0);

  return (
    <div
      data-testid="overlay-rpg-dungeon"
      style={{
        position: "absolute",
        ...placementStyle,
        width: `clamp(${minWidth}px, ${widthVw}vw, ${maxWidth}px)`,
        minHeight: 360,
        padding: 0,
        background: "rgba(9,10,14,0.92)",
        border: "1px solid rgba(255,179,64,0.42)",
        borderRadius: 12,
        boxShadow: "0 20px 60px rgba(0,0,0,0.58), 0 0 38px rgba(255,179,64,0.18)",
        backdropFilter: "blur(10px)",
        zIndex: 13,
        animation: "rpgDungeonIn 260ms ease-out",
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 18px 12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FFB340", fontWeight: 800 }}>
          Dungeon Run
        </div>
        <div style={{ marginLeft: "auto", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: run.status === "running" ? "#30D158" : "#64D2FF", fontWeight: 800 }}>
          {run.status}
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 26, lineHeight: 1.08, fontWeight: 900, color: "#F3EAD3", overflowWrap: "anywhere" }}>
        {run.generated_title || run.quest_title || run.dungeon_name || "Dungeon Run"}
      </div>
      </div>
      {imageEnabled && (
        <div
          aria-hidden="true"
          style={{
            height: Number(display.image_height_px || 260),
            backgroundImage: `linear-gradient(180deg, rgba(8,10,16,0.04), rgba(8,10,16,0.34)), url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: generatedImageUrl ? Math.max(0.2, Math.min(1, Number(display.image_opacity ?? 0.92))) : 0.96,
            borderTop: "1px solid rgba(255,179,64,0.2)",
            borderBottom: "1px solid rgba(255,179,64,0.26)",
          }}
        />
      )}
      <div style={{ padding: "14px 18px 16px" }}>
      <div style={{ height: 9, borderRadius: 999, background: "rgba(255,255,255,0.12)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #FFB340, #BF5AF2)", transition: "width 240ms ease" }} />
      </div>
      {run.status === "assembling" ? (
        <div style={{ marginTop: 14, fontSize: 18, lineHeight: 1.28, color: "#E5E7EB" }}>
          Party forming. Join window closes in <strong>{joinSeconds}s</strong>.
        </div>
      ) : latest ? (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#A1A1AA", fontWeight: 800 }}>
            Step {Number(latest.step || 0) + 1} of {totalSteps}
          </div>
          <div style={{ marginTop: 7, fontSize: 20, lineHeight: 1.28, color: "#FFFFFF", fontWeight: 800, overflowWrap: "anywhere" }}>
            {latest.actor_display} {latest.success ? "overcame" : "was stopped by"} {latest.encounter}
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 13, color: "#D4D4D8" }}>
            <span style={{ padding: "4px 8px", borderRadius: 999, background: latest.success ? "rgba(48,209,88,0.18)" : "rgba(255,69,58,0.18)", color: latest.success ? "#7CFF9B" : "#FF8A80", fontWeight: 800 }}>
              {latest.success ? "Success" : "Failed"}
            </span>
            <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(255,255,255,0.09)" }}>{latest.skill}</span>
            <span style={{ padding: "4px 8px", borderRadius: 999, background: "rgba(255,255,255,0.09)" }}>Roll {latest.roll} vs DC {latest.dc}</span>
          </div>
          {latest.actor_defeated && (
            <div style={{ marginTop: 7, fontSize: 14, color: "#FF8A80", fontWeight: 800 }}>
              Defeated: no dungeon loot or rewards.
            </div>
          )}
          {!latest.actor_defeated && latest.low_health_warning && (
            <div style={{ marginTop: 7, fontSize: 14, color: "#FFB340", fontWeight: 800 }}>
              Low HP: potions recommended before the next run.
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 14, fontSize: 18, lineHeight: 1.28, color: "#E5E7EB" }}>
          The party steps into the dungeon.
        </div>
      )}
      <div style={{ display: "flex", gap: 12, marginTop: 16, fontSize: 13, color: "#D4D4D8" }}>
        <span>{party.length} joined</span>
        <span>{defeated.size} defeated</span>
        <span>{lootCount} loot found</span>
      </div>
      {party.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {party.slice(0, 8).map(member => {
            const name = member.display_name || member.login || "viewer";
            const isDefeated = defeated.has(member.twitch_user_id);
            return (
              <span
                key={member.twitch_user_id || name}
                style={{
                  maxWidth: 118,
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: isDefeated ? "rgba(255,69,58,0.18)" : "rgba(255,255,255,0.1)",
                  color: isDefeated ? "#FF8A80" : "#F3EAD3",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name}
              </span>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

// OBS browser source. Polls for approved redemptions and plays them one at a time.
// IMPORTANT: this view is fully transparent so it composites cleanly in OBS.
// Recommended OBS Browser Source settings: 1920x1080, "Shutdown source when not visible" OFF.
export default function Overlay() {
  const [current, setCurrent] = useState(null);
  const [activeRpgRun, setActiveRpgRun] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [giveawayResult, setGiveawayResult] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const [activePopup, setActivePopup] = useState(null);
  const [prizeSettled, setPrizeSettled] = useState(false);
  const playingRef = useRef(false);
  const queueRef = useRef([]);
  const knownAudioIdsRef = useRef(new Set());
  const activeVideoRef = useRef(null);
  const runtimeExecutingRef = useRef(false);
  const playedAlertAudioRef = useRef(new Set());
  const clearedAlertIdsRef = useRef(new Set());
  const endedAlertMediaRef = useRef({ alertId: null, stepIds: new Set() });

  // Force-transparent body + hide platform overlays (Emergent badge, visual-edits chrome)
  useEffect(() => {
    const prev = {
      bodyBg: document.body.style.background,
      htmlBg: document.documentElement.style.background,
      bodyBgImg: document.body.style.backgroundImage,
      htmlBgImg: document.documentElement.style.backgroundImage,
    };
    document.documentElement.style.background = "transparent";
    document.documentElement.style.backgroundImage = "none";
    document.body.style.background = "transparent";
    document.body.style.backgroundImage = "none";

    const style = document.createElement("style");
    style.setAttribute("data-overlay-style", "true");
    style.textContent = `
      html, body, #root { background: transparent !important; background-image: none !important; }
      #emergent-badge,
      a[href*="emergent.sh"], a[href*="emergentagent.com"],
      [data-emergent-badge], [data-emergent-visual-edits] { display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }
    `;
    document.head.appendChild(style);

    // Aggressively remove the badge node if the platform re-injects it
    const killBadge = () => {
      document.querySelectorAll("#emergent-badge, a[href*='emergent.sh']").forEach((el) => {
        try { el.remove(); } catch (e) { el.style.display = "none"; }
      });
    };
    killBadge();
    const observer = new MutationObserver(killBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.documentElement.style.background = prev.htmlBg;
      document.documentElement.style.backgroundImage = prev.htmlBgImg;
      document.body.style.background = prev.bodyBg;
      document.body.style.backgroundImage = prev.bodyBgImg;
      style.remove();
    };
  }, []);

  useEffect(() => {
    let alive = true;
    const tickRpg = async () => {
      try {
        const { data } = await api.get("/rpg/dungeons/active");
        if (alive) setActiveRpgRun((data || [])[0] || null);
      } catch (e) {
        if (alive) setActiveRpgRun(null);
      }
    };
    tickRpg();
    const id = setInterval(() => { if (alive) tickRpg(); }, 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    const tickAlert = async () => {
      try {
        const { data } = await api.get("/overlay/alerts");
        if (alive) {
          const nextAlert = data.active || null;
          if (nextAlert?.id && clearedAlertIdsRef.current.has(nextAlert.id)) {
            setActiveAlert(null);
            return;
          }
          setActiveAlert(nextAlert);
        }
      } catch (e) {
        if (alive) setActiveAlert(null);
      }
    };
    tickAlert();
    const id = setInterval(() => { if (alive) tickAlert(); }, 500);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    const audioReference = activeAlert?.tts_audio_reference;
    if (!audioReference) return undefined;
    const playKey = `${activeAlert.id || ""}:${audioReference}`;
    if (playedAlertAudioRef.current.has(playKey)) return undefined;
    playedAlertAudioRef.current.add(playKey);

    const audioUrl = audioReference.startsWith("http")
      ? audioReference
      : `${BACKEND_URL}${audioReference}`;
    const audio = new Audio(audioUrl);
    audio.preload = "auto";
    audio.play().catch(() => {
      // Visual alerts stay visible if browser audio playback is blocked or fails.
    });
    return () => {
      audio.pause();
    };
  }, [activeAlert?.id, activeAlert?.tts_audio_reference]);

  const alertStepAudioReferences = useMemo(() => {
    const refs = [];
    for (const step of activeAlert?.steps || []) {
      if (step.step_type === "tts" && step.tts_audio_reference) refs.push(step.tts_audio_reference);
    }
    return refs;
  }, [activeAlert?.steps]);

  const alertMediaStepIds = useMemo(
    () => (activeAlert?.steps || [])
      .filter((step) => step.step_type === "audio" || step.step_type === "video")
      .map((step) => step.id),
    [activeAlert?.steps]
  );

  useEffect(() => {
    endedAlertMediaRef.current = { alertId: activeAlert?.id || null, stepIds: new Set() };
  }, [activeAlert?.id]);

  useEffect(() => {
    if (!activeAlert?.id || alertStepAudioReferences.length === 0) return undefined;
    const playing = [];
    alertStepAudioReferences.forEach((reference) => {
      const playKey = `${activeAlert.id}:${reference}`;
      if (playedAlertAudioRef.current.has(playKey)) return;
      playedAlertAudioRef.current.add(playKey);
      const audioUrl = reference.startsWith("http") ? reference : `${BACKEND_URL}${reference}`;
      const audio = new Audio(audioUrl);
      audio.preload = "auto";
      audio.play().catch(() => {
        // Visual alerts stay visible if browser audio playback is blocked or fails.
      });
      playing.push(audio);
    });
    return () => {
      playing.forEach((audio) => audio.pause());
    };
  }, [activeAlert?.id, alertStepAudioReferences]);

  const clearActiveAlert = async (alertId) => {
    if (alertId) {
      clearedAlertIdsRef.current.add(alertId);
      if (clearedAlertIdsRef.current.size > 100) {
        clearedAlertIdsRef.current = new Set(Array.from(clearedAlertIdsRef.current).slice(-50));
      }
    }
    setActiveAlert(null);
    if (!alertId) return;
    try {
      await api.post(`/overlay/alerts/${alertId}/played`);
    } catch (e) {}
  };

  useEffect(() => {
    let alive = true;
    const tickPopup = async () => {
      try {
        const { data } = await api.get("/overlay/popup");
        if (alive) setActivePopup(data.active || null);
      } catch (e) {
        if (alive) setActivePopup(null);
      }
    };
    tickPopup();
    const id = setInterval(() => { if (alive) tickPopup(); }, 500);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!activePopup?.id) return undefined;
    const durationMs = Math.max(1000, Number(activePopup.duration || 7) * 1000);
    const id = setTimeout(async () => {
      const popupId = activePopup.id;
      setActivePopup(null);
      try {
        await api.post(`/overlay/popup/${popupId}/played`);
      } catch (e) {}
    }, durationMs);
    return () => clearTimeout(id);
  }, [activePopup?.id, activePopup?.duration]);

  const markAlertMediaStepEnded = (stepId) => {
    if (!activeAlert?.id || !stepId || alertMediaStepIds.length === 0) return;
    if (endedAlertMediaRef.current.alertId !== activeAlert.id) {
      endedAlertMediaRef.current = { alertId: activeAlert.id, stepIds: new Set() };
    }
    endedAlertMediaRef.current.stepIds.add(stepId);
    if (endedAlertMediaRef.current.stepIds.size >= alertMediaStepIds.length) {
      clearActiveAlert(activeAlert.id);
    }
  };

  useEffect(() => {
    let alive = true;
    const tickGiveaway = async () => {
      try {
        const { data } = await api.get("/overlay/giveaway-result");
        if (alive) setGiveawayResult(data.active || null);
      } catch (e) {
        if (alive) setGiveawayResult(null);
      }
    };
    tickGiveaway();
    const id = setInterval(() => { if (alive) tickGiveaway(); }, 750);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    setPrizeSettled(false);
    if (giveawayResult?.mode !== "prize_spin") return undefined;
    const id = setTimeout(() => setPrizeSettled(true), 5000);
    return () => clearTimeout(id);
  }, [giveawayResult?.giveaway_id, giveawayResult?.shown_at, giveawayResult?.mode, giveawayResult?.selected_prize]);

  useEffect(() => {
    let alive = true;
    const tickVideo = async () => {
      try {
        const { data } = await api.get("/overlay/video-media/active");
        if (alive) {
          activeVideoRef.current = data.active || null;
          setActiveVideo(data.active || null);
        }
      } catch (e) {
        if (alive) {
          activeVideoRef.current = null;
          setActiveVideo(null);
        }
      }
    };
    tickVideo();
    const id = setInterval(() => { if (alive) tickVideo(); }, 500);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    const tickRuntime = async () => {
      if (runtimeExecutingRef.current || activeVideoRef.current) return;
      runtimeExecutingRef.current = true;
      try {
        await api.post("/runtime/interaction-requests/execute-next-debug", null, { params: { queue_type: "media" } });
        const { data } = await api.get("/overlay/video-media/active");
        if (alive) {
          activeVideoRef.current = data.active || null;
          setActiveVideo(data.active || null);
        }
      } catch (e) {
        // Keep the overlay visually quiet if runtime polling fails.
      } finally {
        runtimeExecutingRef.current = false;
      }
    };
    tickRuntime();
    const id = setInterval(() => { if (alive) tickRuntime(); }, 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const clearActiveVideo = async (eventId) => {
    activeVideoRef.current = null;
    setActiveVideo(null);
    if (!eventId) return;
    try {
      await api.post(`/overlay/video-media/${eventId}/played`);
    } catch (e) {}
  };

  useEffect(() => {
    let alive = true;
    const playNext = () => {
      if (queueRef.current.length === 0 || playingRef.current) return;
      const next = queueRef.current.shift();
      playingRef.current = true;
      setCurrent(next.kind === "narration" ? null : next);
      const done = async () => {
        try {
          const url = next.kind === "narration"
            ? `/rpg/narrations/${next.id}/played`
            : `/overlay/sounds/${next.id}/played`;
          await api.post(url);
        } catch (e) {}
        playingRef.current = false;
        setCurrent(null);
        knownAudioIdsRef.current.delete(next.id);
        setTimeout(playNext, 500);
      };
      if (next.audio_url) {
        const audio = new Audio(`${BACKEND_URL}${next.audio_url}`);
        audio.onended = done;
        audio.onerror = done;
        audio.play().catch(done);
      } else {
        setTimeout(done, next.kind === "narration" ? 5200 : 2400);
      }
    };
    const tick = async () => {
      try {
        const [{ data: sounds }, { data: narrations }] = await Promise.all([
          api.get("/overlay/sounds/pending"),
          api.get("/rpg/narrations/pending"),
        ]);
        for (const item of sounds) {
          if (!knownAudioIdsRef.current.has(item.id)) {
            knownAudioIdsRef.current.add(item.id);
            queueRef.current.push({ ...item, kind: "sound" });
          }
        }
        for (const n of narrations) {
          if (!knownAudioIdsRef.current.has(n.id)) {
            knownAudioIdsRef.current.add(n.id);
            queueRef.current.push({
              id: n.id,
              kind: "narration",
              sound_name: n.text,
              viewer: "Bunny",
              audio_url: n.audio_url,
              audio_status: n.audio_status || n.status,
            });
          }
        }
        if (!playingRef.current) playNext();
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(() => { if (alive) tick(); }, 2500);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div
      data-testid="overlay-root"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "transparent",
        pointerEvents: "none",
        fontFamily: "IBM Plex Sans, sans-serif",
        color: "white",
      }}
    >
      {current && (
        <div
          data-testid="overlay-playing"
          style={{
            position: "absolute",
            left: "50%",
            top: "10%",
            transform: "translateX(-50%)",
            padding: current.kind === "narration" ? "24px 32px" : "18px 28px",
            background: current.kind === "narration"
              ? "linear-gradient(135deg, rgba(12,8,18,0.92), rgba(52,24,88,0.84))"
              : "rgba(10,10,10,0.78)",
            border: `1px solid ${current.kind === "narration" ? "rgba(191,90,242,0.45)" : "rgba(255,255,255,0.15)"}`,
            borderRadius: 10,
            backdropFilter: "blur(8px)",
            boxShadow: current.kind === "narration"
              ? "0 18px 50px rgba(0,0,0,0.55), 0 0 42px rgba(191,90,242,0.34)"
              : "0 10px 30px rgba(0,0,0,0.4)",
            animation: "overlayIn 240ms ease-out",
            maxWidth: current.kind === "narration" ? 840 : 720,
          }}
        >
          <div style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: current.kind === "narration" ? "#BF5AF2" : "#FF9F0A" }}>
            {current.kind === "narration" ? "◆ Bunny narrates" : "▶ Sound Alert"}
          </div>
          <div style={{ fontSize: current.kind === "narration" ? 18 : 28, fontWeight: 500, marginTop: 6, lineHeight: 1.25 }}>{current.sound_name}</div>
          {current.kind !== "narration" && (
            <div style={{ fontSize: 15, color: "#A1A1AA", marginTop: 4 }}>redeemed by {current.viewer}</div>
          )}
        </div>
      )}
      {activeRpgRun && !giveawayResult && !activeAlert && !activePopup && (
        <RPGDungeonOverlay run={activeRpgRun} />
      )}
      {activeVideo && (
        activeVideo.chroma_key?.enabled ? (
          <ChromaKeyVideo
            activeVideo={activeVideo}
            src={`${BACKEND_URL}${activeVideo.media_url}`}
            style={videoPlacementStyle(activeVideo)}
            onEnded={() => clearActiveVideo(activeVideo.id)}
          />
        ) : (
          <video
            key={activeVideo.id}
            data-testid="overlay-video-media"
            data-chroma-key="false"
            src={`${BACKEND_URL}${activeVideo.media_url}`}
            autoPlay
            playsInline
            onEnded={() => clearActiveVideo(activeVideo.id)}
            style={videoPlacementStyle(activeVideo)}
          />
        )
      )}
      {activePopup && <OverlayPopup popup={activePopup} />}
      {activeAlert && (
        <div
          data-testid="overlay-alert"
          style={{
            ...(activeAlert.steps?.length ? { position: "absolute", inset: 0, pointerEvents: "none" } : alertPlacementStyle(activeAlert)),
            padding: activeAlert.steps?.length ? 0 : 22,
            background: activeAlert.steps?.length ? "transparent" : "rgba(8,8,10,0.78)",
            border: activeAlert.steps?.length ? "0" : "1px solid rgba(255,255,255,0.18)",
            borderRadius: activeAlert.steps?.length ? 0 : 10,
            boxShadow: activeAlert.steps?.length ? "none" : "0 18px 48px rgba(0,0,0,0.45)",
            backdropFilter: activeAlert.steps?.length ? "none" : "blur(8px)",
            animation: "alertIn 260ms ease-out",
            zIndex: 18,
            overflow: "hidden",
          }}
        >
          {activeAlert.steps?.length ? (
            <>
              {activeAlert.steps.map((step) => {
                if (step.step_type === "text") {
                  return (
                    <div
                      key={step.id}
                      data-testid="overlay-alert-step-text"
                      style={{
                        ...alertStepPlacementStyle(step),
                        fontSize: step.font_size || 34,
                        lineHeight: 1.12,
                        color: step.color || "#ffffff",
                        fontFamily: step.font_family || "Inter, system-ui, sans-serif",
                        fontWeight: step.font_weight || 800,
                        textAlign: step.text_align || "center",
                        WebkitTextStroke: step.outline_enabled ? `2px ${step.outline_color || "#000000"}` : "0 transparent",
                        textShadow: step.shadow_enabled === false ? "none" : "0 4px 18px rgba(0,0,0,0.75)",
                        background: step.background_enabled ? (step.background_color || "rgba(0,0,0,0.55)") : "transparent",
                        borderRadius: step.border_radius || 0,
                        padding: step.padding || 0,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {step.text}
                    </div>
                  );
                }
                if (step.step_type === "video") {
                  return (
                    <video
                      key={step.id}
                      data-testid="overlay-alert-step-video"
                      src={`${BACKEND_URL}${step.media_url}`}
                      autoPlay
                      muted
                      playsInline
                      onEnded={() => markAlertMediaStepEnded(step.id)}
                      onError={() => markAlertMediaStepEnded(step.id)}
                      style={{
                        ...alertStepPlacementStyle(step),
                        height: step.placement?.height ? `${(step.placement.height / 1080) * 100}%` : "auto",
                        objectFit: "contain",
                      }}
                    />
                  );
                }
                if (step.step_type === "audio") {
                  return (
                    <audio
                      key={step.id}
                      data-testid="overlay-alert-step-audio"
                      src={`${BACKEND_URL}${step.media_url}`}
                      autoPlay
                      onEnded={() => markAlertMediaStepEnded(step.id)}
                      onError={() => markAlertMediaStepEnded(step.id)}
                    />
                  );
                }
                return null;
              })}
            </>
          ) : (
            <>
              {activeAlert.media_url && (
                <video
                  data-testid="overlay-alert-media"
                  src={`${BACKEND_URL}${activeAlert.media_url}`}
                  autoPlay
                  muted
                  playsInline
                  onEnded={() => clearActiveAlert(activeAlert.id)}
                  style={{
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "contain",
                    display: "block",
                    marginBottom: 14,
                    borderRadius: 6,
                  }}
                />
              )}
              <div style={{ fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FFB340", fontWeight: 800 }}>
                {String(activeAlert.event_type || "alert").replace("_", " ")}
              </div>
              <div style={{ marginTop: 6, fontSize: 34, lineHeight: 1.1, fontWeight: 800, color: "#FFFFFF", overflowWrap: "anywhere" }}>
                {activeAlert.title}
              </div>
              <div style={{ marginTop: 8, fontSize: 19, lineHeight: 1.3, color: "#E5E7EB", overflowWrap: "anywhere" }}>
                {activeAlert.message}
              </div>
              {activeAlert.tts_enabled && activeAlert.tts_text && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#A78BFA", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  {activeAlert.tts_status === "generated" ? "TTS playing" : `TTS ${activeAlert.tts_status || "ready"}`}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {giveawayResult && (
        <div
          data-testid="overlay-giveaway-result"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: 620,
            maxWidth: 960,
            padding: "30px 44px",
            textAlign: "center",
            background: "linear-gradient(180deg, rgba(18,18,22,0.9), rgba(8,8,10,0.84))",
            border: "1px solid rgba(191,90,242,0.58)",
            borderRadius: 12,
            boxShadow: "0 20px 70px rgba(0,0,0,0.55), 0 0 54px rgba(191,90,242,0.24)",
            backdropFilter: "blur(10px)",
            animation: "giveawayResultIn 360ms ease-out",
            zIndex: 20,
          }}
        >
          <div
            style={{
              fontSize: 13,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#BF5AF2",
              fontWeight: 700,
            }}
          >
            {giveawayResult.mode === "prize_spin" ? "Prize Wheel" : "Giveaway Winner"}
          </div>
          <div
            style={{
              fontSize: 28,
              lineHeight: 1.15,
              marginTop: 10,
              color: "#F3EAD3",
              fontWeight: 600,
            }}
          >
            {giveawayResult.giveaway_title}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 32, alignItems: "center", marginTop: 24 }}>
            <div>
              <div
                style={{
                  fontSize: 56,
                  lineHeight: 1.05,
                  color: "#FFFFFF",
                  fontWeight: 800,
                  textShadow: "0 4px 26px rgba(191,90,242,0.4)",
                  overflowWrap: "anywhere",
                }}
              >
                {giveawayResult.winner_display_name || giveawayResult.winner_username}
              </div>
              {giveawayResult.winner_username && giveawayResult.winner_display_name !== giveawayResult.winner_username && (
                <div style={{ fontSize: 20, color: "#A1A1AA", marginTop: 10 }}>
                  @{giveawayResult.winner_username}
                </div>
              )}
              <div style={{ marginTop: 22, fontSize: 18, color: "#D4D4D8" }}>
                {giveawayResult.mode === "prize_spin"
                  ? prizeSettled
                    ? "Prize locked in"
                    : "Spinning for the prize..."
                  : "Winner picked. Waiting for the prize spin."}
              </div>
              {giveawayResult.selected_prize && prizeSettled && (
                <div
                  style={{
                    margin: "18px auto 0",
                    display: "inline-block",
                    padding: "12px 20px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,179,64,0.16)",
                    color: "#FFE3A3",
                    fontSize: 24,
                    fontWeight: 800,
                  }}
                >
                  {giveawayResult.selected_prize}
                </div>
              )}
            </div>

            <PrizeWheel
              prizes={giveawayResult.prizes || []}
              selectedPrize={giveawayResult.selected_prize}
              spinning={giveawayResult.mode === "prize_spin"}
            />
          </div>
        </div>
      )}
      <style>{`
        @keyframes overlayIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes alertIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes popupIn { from { opacity: 0; translate: 0 -14px; } to { opacity: 1; translate: 0 0; } }
        @keyframes blackBarTextIn { from { opacity: 0; transform: translateX(-42px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes giveawayResultIn { from { opacity: 0; transform: translate(-50%, -45%) scale(0.96); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
        @keyframes rpgDungeonIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
